import { useEffect } from 'react';
import { Route, useLocation } from 'react-router-dom';
import { unpackRules } from '@casl/ability/extra';

import { AuthenticationService, CaslService, UserRole } from '@/services/api';
import { useGlobalStore } from '@/stores/useGlobalStore';
import { PublicPage } from '@/public/PublicPage';
import { ProtectedLayout } from '@/components/layouts/ProtectedLayout';
import { ConsultingNavigation } from '@/components/navigations/ConsultingNavigation';
import { OrganizationNavigation } from '@/components/navigations/OrganizationNavigation';
import { OrganizationCoreDataPage } from '@/organization/OrganizationCoreDataPage';
import { OverviewPage } from '@/organization/OverviewPage';
import { OrganizationsPage } from '@/consulting/OrganizationsPage';
import { Footer } from '@/components/navigations/Footer';
import { LocationCoreDataPage } from '@/location/LocationCoreDataPage';
import { LocationNavigation } from '@/components/navigations/LocationNavigation';
import { AdminLevelsTreePage } from '@/consulting/AdminLevelsTreePage';
import { CreateOrganizationPage } from '@/consulting/CreateOrganizationPage';
import { NotFoundPage } from '@/public/NotFoundPage';
import { CreateLocationPage } from '@/location/CreateLocationPage';
import { NetworksTreePage } from '@/consulting/NetworksTreePage';
import { CreateNetworkPage } from '@/consulting/CreateNetworkPage';
import { AcceptInvitationPage } from '@/authentication/accept-invitation/AcceptInvitationPage';
import { PrivacyPage } from '@/public/PrivacyPage';
import { InterestPage } from '@/public/InterestPage';
import { ImprintPage } from '@/public/ImprintPage';
import { ContactPage } from '@/public/ContactPage';
import { LocationProfilePage } from '@/location/LocationProfilePage';
import { LocationProfilePrint } from '@/location/LocationProfilePrint';
import { NetworkPage } from '@/consulting/NetworkPage';
import { IndicatorProcessesPage } from '@/indicator-process/IndicatorProcessesPage';
import { CreateIndicatorProcessPage } from '@/indicator-process/CreateIndicatorProcessPage';
import { IndicatorProcessPage } from '@/indicator-process/IndicatorProcessPage';
import { UsersPage } from '@/consulting/users/UsersPage';
import { CreateUserPage } from '@/consulting/users/CreateUserPage';
import { UserPage } from '@/consulting/users/UserPage';
import { useAppAbility } from '@/hooks/useAppAbility';
import { OrganizationUsersPage } from '@/organization-users/OrganizationUsersPage';
import { CreateOrganizationUserPage as CreateOrganizationUserPageOrganization } from '@/organization-users/CreateOrganizationUserPage';
import { CreateOrganizationUserPage as CreateOrganizationUserPageUser } from '@/consulting/users/CreateOrganizationUserPage';
import { ChangePasswordPage } from '@/general/ChangePasswordPage';
import { IndicatorRoutingConfigurationPage } from '@/indicator-process/IndicatorRoutingConfigurationPage';
import { AnalysisPage } from '@/organization/AnalysisPage';
import { LocationsPage } from '@/consulting/LocationsPage';
import { ForgotPasswordPage } from '@/authentication/password-reset/ForgotPasswordPage';
import { ResetPasswordPage } from '@/authentication/password-reset/ResetPasswordPage';
import { HelpPage } from '@/public/HelpPage';
import { MonitoringPage } from '@/monitoring/MonitoringPage';
import { MonitoringDailyPage } from '@/monitoring/MonitoringDailyPage';
import { MonitoringConfigurationPage } from '@/monitoring/MonitoringConfigurationPage';
import { MonitoringAnalysisPage } from '@/monitoring/MonitoringAnalysisPage';
import { SentryRoutes } from '@/setup/sentry';

export const App = () => {
  const { setUser } = useGlobalStore((state) => state.actions);
  const ability = useAppAbility();
  const { pathname } = useLocation();

  useEffect(() => {
    (async () => {
      try {
        setUser(await AuthenticationService.getProfile());
        ability.update(unpackRules((await CaslService.getRules()).rules as any));
      } catch {
        setUser(undefined);
        ability.update([]);
      }
    })();
  }, [ability, setUser]);

  return (
    <>
      <SentryRoutes>
        <Route path="/" element={<PublicPage />} />
        <Route path="/interesse" element={<InterestPage />} />
        <Route path="/kontakt" element={<ContactPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/datenschutz" element={<PrivacyPage />} />
        <Route path="/impressum" element={<ImprintPage />} />
        <Route path="/einladung/:userId" element={<AcceptInvitationPage />} />

        <Route path="passwort">
          <Route path="vergessen" element={<ForgotPasswordPage />} />
          <Route path="zuruecksetzen" element={<ResetPasswordPage />} />
        </Route>

        <Route path="/passwort-aendern" element={<ProtectedLayout allowedRoles={Object.values(UserRole)} />}>
          <Route index element={<ChangePasswordPage />} />
        </Route>

        <Route
          path="beratung"
          element={
            <ProtectedLayout
              allowedRoles={[UserRole.TRAFFICON_ADMIN, UserRole.IVM_ADMIN, UserRole.IVM_USER]}
              navigation={<ConsultingNavigation />}
            />
          }
        >
          <Route path="organisation" element={<OrganizationsPage />} />
          <Route path="organisation/erstellen" element={<CreateOrganizationPage />} />

          <Route path="anwendungsfaelle" element={<LocationsPage />} />

          <Route path="benutzerinnen" element={<UsersPage />} />
          <Route path="benutzerinnen/erstellen" element={<CreateUserPage />} />
          <Route path="benutzerinnen/:userId" element={<UserPage />} />
          <Route path="benutzerinnen/:userId/zuordnen" element={<CreateOrganizationUserPageUser />} />

          <Route path="raeumliche-ebenen" element={<AdminLevelsTreePage />} />

          <Route path="kategorien" element={<NetworksTreePage />} />
          <Route path="kategorien/erstellen" element={<CreateNetworkPage />} />
          <Route path="kategorien/:networkId" element={<NetworkPage />} />

          <Route path="wsa-berechnung" element={<IndicatorProcessesPage />} />
          <Route path="wsa-berechnung/erstellen" element={<CreateIndicatorProcessPage />} />
          <Route path="wsa-berechnung/konfigurieren" element={<IndicatorRoutingConfigurationPage />} />
          <Route path="wsa-berechnung/:indicatorProcessId" element={<IndicatorProcessPage />} />

          <Route path="monitoring-analysis" element={<MonitoringAnalysisPage />} />
        </Route>

        <Route
          path="/organisation/:organizationId/anwendungsfaelle"
          element={<ProtectedLayout allowedRoles={Object.values(UserRole)} navigation={<LocationNavigation />} />}
        >
          <Route path="erstellen" element={<CreateLocationPage />} />
          <Route path=":locationId" element={<LocationCoreDataPage />} />
          <Route path=":locationId/steckbrief" element={<LocationProfilePage />} />
          <Route path=":locationId/monitoring-einstellung" element={<MonitoringConfigurationPage />} />
        </Route>

        <Route path="/monitoring/:monitoringId">
          <Route path="" element={<MonitoringPage />} />
          <Route path="daily" element={<MonitoringDailyPage />} />
        </Route>

        <Route
          path="/organisation/:organizationId/anwendungsfaelle/:locationId/steckbrief/drucken"
          element={<LocationProfilePrint />}
        />

        <Route
          path="/organisation"
          element={<ProtectedLayout allowedRoles={Object.values(UserRole)} navigation={<OrganizationNavigation />} />}
        >
          <Route index element={<OrganizationsPage />} />
          <Route path=":organizationId" element={<OverviewPage />} />
          <Route path=":organizationId/stammdaten" element={<OrganizationCoreDataPage />} />
          <Route path=":organizationId/benutzerinnen" element={<OrganizationUsersPage />} />
          <Route path=":organizationId/benutzerinnen/zuordnen" element={<CreateOrganizationUserPageOrganization />} />
          <Route path=":organizationId/wohnstandortanalyse/" element={<AnalysisPage />} />
          <Route path=":organizationId/wohnstandortanalyse/:indicatorProcessId" element={<AnalysisPage />} />
          <Route path=":organizationId/monitoring-analysis" element={<MonitoringAnalysisPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </SentryRoutes>

      {!pathname.includes('monitoring') && <Footer />}
    </>
  );
};
